import { Socket, io } from 'socket.io-client';
import { IRealtimeClient } from './realtime-client.interface';
import { Observable, Subject } from 'rxjs';
import { resolveGalactusSocketConfig } from './galactus-socket-url';

interface RealtimeMessageResponse {
  channel: string;
  payload: any;
}

export class GalactusClient implements IRealtimeClient {
  private client: Socket;
  private _galactusClientDisconnected$ = new Subject<boolean>();
  private tokens = new Map<string, string>();
  private subscriptionCallbacks = new Map<string, Array<(msg: any) => void>>();
  private MAX_ACK_TIMEOUT = 10000;
  private MAX_RETRY_ATTEMPTS = 5;
  private EXP_BACKOFF_SCALING_FACTOR = 200;
  private isFirstConnection = true;

  constructor(url: string) {
    const { origin, path } = resolveGalactusSocketConfig(url);
    this.client = io(origin, {
      ackTimeout: this.MAX_ACK_TIMEOUT,
      path,
      transports: ['websocket']
    });

    this.client.on('disconnect', (reason, desc) => {
      console.log('[GALACTUS] DISCONNECT: ', reason, desc);
      this._galactusClientDisconnected$.next(true);
    });

    this.client.on('connect_error', (error) => {
      console.error('[GALACTUS] CONNECTION ERROR: ', error);
    });

    this.client.on('error', (error) => {
      console.error('[GALACTUS] ERROR: ', error);
    });

    this.client.on('connect', async () => {
      if (this.isFirstConnection) {
        this.isFirstConnection = false;
        console.log('[GALACTUS] CONNECTED');
      } else {
        // emitting true first to handle cases when reconnection happens without triggering disconnect event
        this._galactusClientDisconnected$.next(true);
        console.log('[GALACTUS] RECONNECTED');
      }

      const resubscriptions: Promise<void>[] = [];
      for (const channel of this.subscriptionCallbacks.keys()) {
        const resubscribe = async (): Promise<void> => {
          try {
            const token = this.tokens.get(channel) ?? '';
            await this._subscribeToChannel(channel, token);
            console.log(
              `[GALACTUS] Resubscribed to channel: ${channel} successfully`
            );
          } catch (err) {
            console.error(
              `[GALACTUS] Could not resubscribe to channel: ${channel}, reason: `,
              err
            );
          }
        };
        resubscriptions.push(resubscribe());
      }
      await Promise.allSettled(resubscriptions);

      this._galactusClientDisconnected$.next(false);
    });

    this.client.on('message', (data: RealtimeMessageResponse) => {
      const { channel, payload } = data;
      const callbacks = this.subscriptionCallbacks.get(channel);
      callbacks?.forEach((callback) => callback(payload));
    });
  }

  unsubscribeFromChannel(channel: string): void {
    const token = this.tokens.get(channel);
    if (!token) {
      console.log(
        `Galactus canceling unsubscription for channel: ${channel} because no token was found`
      );
      return;
    }

    this.client.emit('unsubscribe', {
      channel: channel,
      token: token
    });
    this.subscriptionCallbacks.delete(channel);
    this.tokens.delete(channel);
    console.log(`Galactus unsubscribed from ${channel}`);
  }

  async subscribeToChannel(
    channel: string,
    token: string,
    callback: (message: any) => void
  ): Promise<void> {
    try {
      // storing token and adding to subscription to resubscribe after connection/reconnection
      this.tokens.set(channel, token);
      if (!this.subscriptionCallbacks.has(channel)) {
        this.subscriptionCallbacks.set(channel, []);
      }
      this.subscriptionCallbacks.get(channel)?.push(callback);

      await this._subscribeToChannel(channel, token);
      console.log(`Galactus subscribed to realtime channel: ${channel}`);
    } catch (err) {
      console.error(
        `Galactus failed to subscribe to realtime channel ${channel} reason : `,
        err
      );
      throw err;
    }
  }

  async publish(channel: string, token: string, message: any): Promise<void> {
    const { error } = await this.client.emitWithAck('publish', {
      channel: channel,
      token: token,
      payload: message
    });
    if (error) {
      throw new Error(error);
    }
  }

  realtimeClientDisconnected$(): Observable<boolean> {
    return this._galactusClientDisconnected$.asObservable();
  }

  disconnect(): void {
    this.client.disconnect();
  }

  private _sleep(time: number) {
    return new Promise<void>((resolve) => {
      setTimeout(resolve, time);
    });
  }

  private async _subscribeToChannel(
    channel: string,
    token: string
  ): Promise<void> {
    for (let attempt = 0; attempt < this.MAX_RETRY_ATTEMPTS; attempt++) {
      let result;
      try {
        result = await this.client.emitWithAck('subscribe', {
          channel: channel,
          token: token
        });
      } catch (err) {
        console.log(
          `Galactus failed subscribing to channel: ${channel}. attempt ${
            attempt + 1
          } of ${this.MAX_RETRY_ATTEMPTS}`,
          err
        );

        // If this was the last attempt, throw the error
        if (attempt === this.MAX_RETRY_ATTEMPTS - 1) {
          throw err;
        }

        const backOffTime =
          Math.pow(2, attempt) * this.EXP_BACKOFF_SCALING_FACTOR;
        await this._sleep(backOffTime);
        continue;
      }
      if (result?.status === 'success') {
        break;
      }

      throw new Error(result.error);
    }
  }
}
